/* ==========================================================================
   DTC B2B, storefront helpers on top of SparkLayer
   Loaded (deferred) from snippets/stylesheets.liquid.

   Prices:  window.spark.getPricingForVariant(productId, sku)
   Cart:    window.spark.updateCart({ products: [{ sku, adjustQuantity }] })
   Variant: 'spark-variant-change' event on <spark-product-card>

   Count:   spark.options.onCartUpdate / onCartLoad (SparkLayer hooks) for the
            line count in the header

   Parts
     1. helpers (money, SparkLayer ready, price queue, add to order)
     2. SparkLayer styling hooks (variables and square corners) and slots
     3. trade pack line on product cards, RRP
     4. image placeholder for products without an image
     5. article index (trade pack SKU, original article number, EAN), home
        page quick order and reorder
     6. collection page: table view, grid view sort, storefront filters
     7. header: number of lines in the SparkLayer order
     8. search page: original article number matches
     9. order page in SparkLayer's drawer: document buttons
    10. returns page
   ========================================================================== */
(function () {
  'use strict';

  /* ---------- 1. helpers ---------- */

  function money(amount, currency) {
    var cur = String(currency || 'EUR').toUpperCase();
    try {
      return new Intl.NumberFormat(cur === 'GBP' ? 'en-GB' : 'nl-NL', { style: 'currency', currency: cur }).format(amount);
    } catch (e) {
      return cur + ' ' + Number(amount).toFixed(2);
    }
  }

  function digits(value) {
    var m = String(value == null ? '' : value).match(/(\d+)\s*$/);
    return m ? m[1] : '';
  }

  /* SparkLayer readiness. The SDK functions exist before SparkLayer has
     finished initialising, and calls made then can hang forever. Ready means
     spark.isLoggedIn() (documented, returns a promise) has resolved true.
     Every probe has a timeout, and we give up after SPARK_DEADLINE. */
  var SPARK_DEADLINE = 45000;
  var sparkWaiters = [];
  var sparkState = 'idle';
  var sparkStarted = 0;

  function delay(ms) { return new Promise(function (ok) { setTimeout(ok, ms); }); }

  /* settles with the promise, or rejects with err.timeout after ms */
  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () {
        var err = new Error('SparkLayer did not answer');
        err.timeout = true;
        reject(err);
      }, ms);
      Promise.resolve(promise).then(function (v) { clearTimeout(timer); resolve(v); },
        function (e) { clearTimeout(timer); reject(e); });
    });
  }

  function sparkSettle(ok) {
    sparkState = ok ? 'ready' : 'failed';
    sparkWaiters.splice(0).forEach(function (w) {
      if (ok) w.resolve(window.spark); else w.reject(new Error('SparkLayer not available'));
    });
  }

  function pollSpark() {
    var again = function (ms) {
      if (Date.now() - sparkStarted > SPARK_DEADLINE) sparkSettle(false);
      else setTimeout(pollSpark, ms);
    };
    var spark = window.spark;
    if (spark && spark.options) {
      hookSparkCart(spark);
      enableSparkSlots(spark);
    }
    if (!(spark && typeof spark.getPricingForVariant === 'function')) return again(500);
    if (typeof spark.isLoggedIn !== 'function') return sparkSettle(true);
    withTimeout(new Promise(function (ok) { ok(spark.isLoggedIn()); }), 5000).then(function (loggedIn) {
      if (loggedIn) sparkSettle(true); else again(1000);
    }, function () { again(1000); });
  }

  function whenSpark() {
    if (sparkState === 'ready') return Promise.resolve(window.spark);
    if (sparkState === 'failed') return Promise.reject(new Error('SparkLayer not available'));
    return new Promise(function (resolve, reject) {
      sparkWaiters.push({ resolve: resolve, reject: reject });
      if (sparkState === 'idle') {
        sparkState = 'waiting';
        sparkStarted = Date.now();
        pollSpark();
      }
    });
  }

  /* Price lookups, at most four at a time. Each call times out so a hung call
     frees its slot, and is retried up to PRICE_RETRIES times. The first calls
     after load can hang while SparkLayer settles, so the first attempt gives
     up after PRICE_FIRST_TIMEOUT and is retried at once; later attempts wait
     PRICE_TIMEOUT. Only prices and real "no price" answers are cached. */
  var PRICE_FIRST_TIMEOUT = 3000;
  var PRICE_TIMEOUT = 8000;
  var PRICE_RETRIES = 2;
  var priceCache = {};
  var queue = [];
  var active = 0;

  function pump() {
    while (active < 4 && queue.length) {
      (function (job) {
        active++;
        job.run().then(job.resolve, job.reject).then(function () { active--; pump(); });
      })(queue.shift());
    }
  }

  function queued(run) {
    return new Promise(function (resolve, reject) {
      queue.push({ run: run, resolve: resolve, reject: reject });
      pump();
    });
  }

  function priceAttempt(pid, sku, n) {
    return whenSpark().then(function (spark) {
      return queued(function () {
        return withTimeout(new Promise(function (ok) { ok(spark.getPricingForVariant(String(pid), String(sku))); }),
          n === 0 ? PRICE_FIRST_TIMEOUT : PRICE_TIMEOUT);
      });
    }).then(function (data) {
      /* null, empty or zero means this dealer has no trade price for the article */
      var price = data && data.price != null && data.price !== '' ? Number(data.price) : NaN;
      if (!(price > 0)) { var err = new Error('No price'); err.noPrice = true; throw err; }
      return { price: price, currency: data.currencyCode || 'EUR' };
    }).catch(function (err) {
      if (err.noPrice || n >= PRICE_RETRIES || sparkState === 'failed') throw err;
      return delay(n === 0 ? 0 : 1500 * n).then(function () { return priceAttempt(pid, sku, n + 1); });
    });
  }

  function getPrice(pid, sku) {
    if (!pid || !sku) return Promise.reject(new Error('No article number'));
    var key = pid + '|' + sku;
    if (!priceCache[key]) {
      var p = priceAttempt(pid, sku, 0);
      priceCache[key] = p;
      p.catch(function (err) {
        if (!err.noPrice && priceCache[key] === p) delete priceCache[key];
      });
    }
    return priceCache[key];
  }

  /* updateCart resolves (never rejects) with one result per line, or null when
     the whole request failed. A line counts as added when its messages carry
     no failure type. */
  var CART_FAILURES = ['not-found', 'unavailable', 'qty-unavailable', 'qty-unavailable-no-clamp', 'excessive-lines'];

  function lineAdded(result) {
    if (!result) return false;
    var msgs = result.messages || [];
    for (var i = 0; i < msgs.length; i++) {
      var type = msgs[i] && (msgs[i].type || msgs[i].messageType || msgs[i]);
      if (CART_FAILURES.indexOf(String(type)) !== -1) return false;
    }
    return !(result.resultantQuantity === 0);
  }

  /* lines: [{ sku, qty }]. Resolves with the number of lines that really went
     into the SparkLayer cart, rejects when the cart could not be updated. */
  function addToOrder(lines) {
    var merged = {};
    lines.forEach(function (l) {
      if (!l.sku || !(l.qty > 0)) return;
      merged[l.sku] = (merged[l.sku] || 0) + l.qty;
    });
    var products = Object.keys(merged).map(function (sku) {
      return { sku: sku, adjustQuantity: merged[sku] };
    });
    if (!products.length) return Promise.resolve(0);
    return whenSpark().then(function (spark) {
      if (typeof spark.updateCart !== 'function') throw new Error('SparkLayer cart not available');
      return spark.updateCart({ products: products });
    }).then(function (results) {
      if (!results) throw new Error('SparkLayer cart update failed');
      if (!Array.isArray(results)) return products.length;
      return results.filter(lineAdded).length;
    });
  }

  function lineWord(n) { return n === 1 ? '1 line' : n + ' lines'; }

  /* ---------- 2. SparkLayer styling hooks and slots ---------- */

  /* SparkLayer shows a custom slot only when it is on this list; the content
     is in snippets/dtc-spark-slots.liquid. Set again on later cart events, in
     case SparkLayer replaces its options when its remote settings arrive. */
  var SPARK_SLOTS = ['spark-slot-purchase-summary-header', 'spark-slot-my-account'];

  function enableSparkSlots(spark) {
    var display = spark && spark.options && spark.options.display;
    if (!display) return;
    var on = Array.isArray(display.showSlots) ? display.showSlots : [];
    var missing = SPARK_SLOTS.filter(function (id) { return on.indexOf(id) === -1; });
    if (missing.length) display.showSlots = on.concat(missing);
  }

  var SHADOW_CSS = '*,*::before,*::after{border-radius:0!important}' +
    '.dtc-cart-notice,.dtc-cart-notice-box{margin:0;padding:0 0 0 10px;border:0;border-inline-start:2px solid #EE8A1F;background:none}' +
    '.dtc-cart-notice,.dtc-cart-notice-box p{color:#5C5C52;font-size:12px;line-height:1.4;text-align:start}';
  var shadowSheet = null;
  try {
    shadowSheet = new CSSStyleSheet();
    shadowSheet.replaceSync(SHADOW_CSS);
  } catch (e) { shadowSheet = null; }

  function styleShadow(root) {
    if (root.__dtcStyled) return;
    root.__dtcStyled = true;
    try {
      if (shadowSheet && 'adoptedStyleSheets' in root) {
        root.adoptedStyleSheets = Array.prototype.concat.call(root.adoptedStyleSheets, [shadowSheet]);
      } else {
        var s = document.createElement('style');
        s.textContent = SHADOW_CSS;
        root.appendChild(s);
      }
    } catch (e) {}
    markSpark(root);
    placeCartNotice(root);
    try {
      new MutationObserver(function () {
        markSpark(root);
        placeCartNotice(root);
      }).observe(root, { childList: true, subtree: true });
    } catch (e) {}
  }

  /* The backorder notice, text from layout/theme.liquid. Review & Pay shows it
     as SparkLayer's own checkout message; this only restyles that box. My Cart
     has no such message or slot, so there it goes straight into the drawer,
     under the totals and above the Checkout button. Should SparkLayer change
     this markup, the My Cart notice stops showing and checkout carries on. */
  function placeCartNotice(root) {
    var text = window.dtcCartNotice;
    if (!text || !root.host || root.host.localName !== 'spark-drawer') return;
    var lines = root.querySelectorAll('p:not(.dtc-cart-notice)');
    for (var p = 0; p < lines.length; p++) {
      if (lines[p].textContent.trim() === text) lines[p].parentElement.classList.add('dtc-cart-notice-box');
    }
    var buttons = root.querySelectorAll('button');
    for (var i = 0; i < buttons.length; i++) {
      if (!/^checkout$/i.test(buttons[i].textContent.trim())) continue;
      var row = buttons[i].parentElement;
      var panel = row && row.parentElement;
      if (!panel || panel.querySelector(':scope > .dtc-cart-notice')) return;
      var notice = document.createElement('p');
      notice.className = 'dtc-cart-notice';
      notice.textContent = text;
      panel.insertBefore(notice, row);
      return;
    }
  }

  /* scope itself plus its descendants matching selector */
  function within(scope, selector) {
    var list = Array.prototype.slice.call(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) list.unshift(scope);
    return list;
  }

  function markSpark(scope) {
    var all = within(scope, '*');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.localName.indexOf('spark-') !== 0) continue;
      if (!el.hasAttribute('data-dtc-spark')) el.setAttribute('data-dtc-spark', '');
      if (el.shadowRoot) styleShadow(el.shadowRoot);
    }
  }

  /* ---------- 3. trade pack line on product cards ---------- */

  function packData(el) {
    if (!el.__dtcData) {
      try {
        el.__dtcData = JSON.parse(el.querySelector('[data-dtc-pack-data]').textContent);
      } catch (e) { el.__dtcData = []; }
    }
    return el.__dtcData;
  }

  function renderPack(el, variantId) {
    var list = packData(el);
    var vid = digits(variantId || el.getAttribute('data-vid'));
    var variant = null;
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === vid) { variant = list[i]; break; }
    }
    if (!variant) variant = list[0];
    if (!variant) return;

    var pack = variant.pack > 1 ? variant.pack : 1;
    var size = el.querySelector('[data-dtc-pack-size]');
    var unit = el.querySelector('[data-dtc-pack-unit]');
    var note = el.querySelector('[data-dtc-pack-note]');
    el.setAttribute('data-vid', variant.id);
    if (size) size.textContent = pack > 1 ? 'Trade pack of ' + pack : 'Sold per unit';
    if (note) note.hidden = pack < 2;
    if (!unit) return;
    unit.hidden = true;
    if (pack < 2 || !variant.sku) return;

    getPrice(el.getAttribute('data-pid'), variant.sku).then(function (p) {
      if (String(el.getAttribute('data-vid')) !== String(variant.id)) return;
      unit.textContent = money(p.price / pack, p.currency) + ' per unit';
      unit.hidden = false;
    }).catch(function () {});
  }

  function initPacks(scope) {
    var els = within(scope, '[data-dtc-pack]:not([data-dtc-ready])');
    for (var i = 0; i < els.length; i++) {
      els[i].setAttribute('data-dtc-ready', '');
      renderPack(els[i]);
    }
  }

  /* SparkLayer fires this on <spark-product-card> when the dropdown changes */
  document.addEventListener('spark-variant-change', function (e) {
    var d = e.detail || e.data || {};
    var pid = digits(d.product && (d.product.externalId || d.product.id));
    var vid = digits(d.variant && (d.variant.externalId || d.variant.id));
    if (!vid) return;
    var card = e.target && e.target.closest ? e.target.closest('product-card, .dtc-card, li, article') : null;
    var els = card ? card.querySelectorAll('[data-dtc-pack]') : [];
    if (!els.length && pid) els = document.querySelectorAll('[data-dtc-pack][data-pid="' + pid + '"]');
    for (var i = 0; i < els.length; i++) renderPack(els[i], vid);
  }, true);

  /* RRP: the Shopify variant price is the consumer price per order unit, in EUR
     (trade prices live in SparkLayer only). A customer on another price list,
     such as GBP for UK dealers, sees no RRP until there is one in their currency.
     The currency of the customer's trade price decides, so this also holds for a
     sales rep serving a dealer. No trade price means no RRP. */
  function rrpShown(cents, currency) { return Number(cents) > 0 && String(currency).toUpperCase() === 'EUR'; }

  function rrpAmount(cents) { return 'EUR ' + (Number(cents) / 100).toFixed(2); }

  /* product page: <span data-dtc-rrp data-pid data-sku data-rrp data-pack hidden> */
  function initRrp(scope) {
    var els = within(scope, '[data-dtc-rrp]:not([data-dtc-ready])');
    for (var i = 0; i < els.length; i++) {
      (function (el) {
        el.setAttribute('data-dtc-ready', '');
        var cents = Number(el.getAttribute('data-rrp'));
        var pack = parseInt(el.getAttribute('data-pack'), 10) || 1;
        getPrice(el.getAttribute('data-pid'), el.getAttribute('data-sku')).then(function (p) {
          if (!rrpShown(cents, p.currency)) return;
          el.textContent = 'RRP ' + rrpAmount(cents);
          if (pack > 1) {
            var piece = document.createElement('span');
            piece.className = 'dtc-rrp-piece';
            piece.textContent = 'RRP per piece ' + rrpAmount(cents / pack);
            el.appendChild(piece);
          }
          el.hidden = false;
          var row = el.closest('tr[hidden]');
          if (row) row.hidden = false;
        }).catch(function () {});
      })(els[i]);
    }
  }

  /* ---------- 4. image placeholder ---------- */

  function initPlaceholders(scope) {
    var els = within(scope, '.product-card-gallery__title-placeholder .title-text:not([data-dtc-ready])');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      el.setAttribute('data-dtc-ready', '');
      var card = el.closest('product-card, li, article');
      var pack = card ? card.querySelector('[data-dtc-pack][data-vendor]') : null;
      var vendor = pack ? pack.getAttribute('data-vendor') : '';
      if (vendor) el.textContent = vendor;
    }
  }

  /* ---------- 5. article index, home page quick order and reorder ---------- */

  function fetchJson(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('Request failed');
      return r.json();
    });
  }

  /* Every trade pack variant on the Online Store that SparkLayer shows to
     customer group base, from templates/collection.dtc-article-index.liquid:
     [sku, original article number, ean, product id, handle, title, variant
     title, pack, variant id, brand, image, available, customer groups the
     variant is hidden from, rrp in cents]. Shopify search does not index the original
     article number (variant metafield b2b.base_sku), so the quick order and
     the search page look codes up here, and the order table filter searches
     all brands in it. Kept in sessionStorage for INDEX_TTL. */
  var INDEX_KEY = 'dtcArticleIndex:3';
  var INDEX_TTL = 30 * 60 * 1000;
  var indexPromise = null;

  function norm(code) { return String(code == null ? '' : code).trim().toLowerCase(); }
  function eanKey(code) { return norm(code).replace(/^0+/, ''); }

  function buildIndex(items) {
    var idx = { sku: {}, base: {}, ean: {}, all: [] };
    items.forEach(function (it) {
      var e = {
        sku: it[0], base: it[1], ean: it[2], pid: it[3], handle: it[4], title: it[5], variant: it[6], pack: it[7], vid: it[8],
        brand: it[9], img: it[10], available: it[11] !== 0, hiddenFrom: it[12] || [], rrp: it[13] || 0
      };
      e.name = norm(e.title + ' ' + e.variant);
      e.hay = norm(e.name + ' ' + e.sku + ' ' + e.base + ' ' + e.ean);
      idx.all.push(e);
      if (e.sku) idx.sku[norm(e.sku)] = e;
      String(e.base || '').split(/\s+/).filter(Boolean).forEach(function (b) {
        (idx.base[norm(b)] = idx.base[norm(b)] || []).push(e);
      });
      if (e.ean) (idx.ean[eanKey(e.ean)] = idx.ean[eanKey(e.ean)] || []).push(e);
    });
    return idx;
  }

  function fetchIndexPage(page) {
    return fetch('/collections/all?view=dtc-article-index&page=' + page, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('Request failed'); return r.text(); })
      .then(function (text) { return JSON.parse(text); });
  }

  function loadArticleIndex() {
    if (indexPromise) return indexPromise;
    try {
      var c = JSON.parse(window.sessionStorage.getItem(INDEX_KEY) || 'null');
      if (c && Date.now() - c.t < INDEX_TTL && Array.isArray(c.items)) return (indexPromise = Promise.resolve(buildIndex(c.items)));
    } catch (e) {}
    indexPromise = fetchIndexPage(1).then(function (first) {
      var jobs = [];
      for (var p = 2; p <= (first.pages || 1); p++) jobs.push(fetchIndexPage(p));
      return Promise.all(jobs).then(function (rest) {
        var items = rest.reduce(function (all, r) { return all.concat(r.items || []); }, first.items || []);
        try { window.sessionStorage.setItem(INDEX_KEY, JSON.stringify({ t: Date.now(), items: items })); } catch (e) {}
        return buildIndex(items);
      });
    });
    indexPromise.catch(function () { indexPromise = null; });
    return indexPromise;
  }

  /* { how: 'sku' | 'ean' | 'base', entries: [...] }, or null */
  function lookupArticle(idx, code) {
    var k = norm(code);
    if (!k) return null;
    if (idx.sku[k]) return { how: 'sku', entries: [idx.sku[k]] };
    if (/^\d{8,14}$/.test(k) && idx.ean[eanKey(k)]) return { how: 'ean', entries: idx.ean[eanKey(k)] };
    if (idx.base[k]) return { how: 'base', entries: idx.base[k] };
    return null;
  }

  function packWord(e) { return e.pack > 1 ? 'trade pack of ' + e.pack : 'sold per unit'; }

  function splitTerms(text) { return norm(text).split(/\s+/).filter(Boolean); }

  function matchTerms(hay, terms) {
    for (var t = 0; t < terms.length; t++) {
      if (hay.indexOf(terms[t]) === -1) return false;
    }
    return true;
  }

  /* The customer group SparkLayer applies right now: the dealer's own, or for
     a sales rep the group of the dealer being served (spark.fetch adds that
     dealer). Resolves null when SparkLayer does not answer. */
  var groupPromise = null;

  function customerGroup() {
    if (groupPromise) return groupPromise;
    groupPromise = whenSpark().then(function (spark) {
      return withTimeout(spark.fetch(
        'query dtcCustomerGroup($impersonatingCustomerId: String) { loggedInCustomer(impersonatingCustomerId: $impersonatingCustomerId) { group } }',
        {}
      ), 8000);
    }).then(function (r) { return r.json(); }).then(function (d) {
      return d.data.loggedInCustomer.group || null;
    }).catch(function () { groupPromise = null; return null; });
    return groupPromise;
  }

  /* Hidden variants never show, and dealers in a ...-zonder-fishex group never
     see FishEx. While the group is unknown, anything hidden from any group
     stays out. */
  function hiddenFromGroup(hiddenFrom, group) {
    return group ? hiddenFrom.indexOf(group) !== -1 : hiddenFrom.length > 0;
  }

  function visibleToGroup(e, group) {
    if (hiddenFromGroup(e.hiddenFrom, group)) return false;
    return !(group && /-zonder-fishex$/.test(group) && /fishex/i.test(e.brand));
  }

  /* Article number or EAN to the SKU of a trade pack on the Online Store,
     through predictive search and the product JSON. Resolves null when no
     trade pack matches, rejects when the lookup itself failed. Single units
     for sales reps are only published on the SparkLayer channel, so they are
     never found here: that is how the quick order tells them apart. */
  function findTradePack(code, isEan) {
    var wanted = String(code).toLowerCase();
    var url = '/search/suggest.json?q=' + encodeURIComponent(code) +
      '&resources[type]=product&resources[limit]=10&resources[options][fields]=' +
      (isEan ? 'variants.barcode' : 'variants.sku');
    return fetchJson(url).then(function (data) {
      var products = (((data || {}).resources || {}).results || {}).products || [];
      var next = function (i) {
        if (i >= products.length) return null;
        return fetchJson('/products/' + products[i].handle + '.js').then(function (p) {
          var vs = p.variants || [];
          for (var k = 0; k < vs.length; k++) {
            var value = String((isEan ? vs[k].barcode : vs[k].sku) || '').toLowerCase();
            if (value === wanted && vs[k].sku) return vs[k].sku;
          }
          return next(i + 1);
        }, function () { return next(i + 1); });
      };
      return next(0);
    });
  }

  /* map with at most `limit` jobs running, results in input order */
  function mapLimit(items, limit, fn) {
    var results = new Array(items.length);
    var index = 0;
    function worker() {
      if (index >= items.length) return Promise.resolve();
      var i = index++;
      return Promise.resolve(fn(items[i])).then(function (r) { results[i] = r; return worker(); });
    }
    var workers = [];
    for (var w = 0; w < Math.min(limit, items.length); w++) workers.push(worker());
    return Promise.all(workers).then(function () { return results; });
  }

  function parseQuickOrder(text) {
    var lines = [];
    String(text || '').split(/\r?\n/).forEach(function (raw) {
      var parts = raw.trim().split(/[\s;,]+/).filter(Boolean);
      if (!parts.length) return;
      var first = parts[0];
      var qty = 1;
      if (parts.length > 1) {
        /* a number, 5x or x5 is a quantity; 12mm belongs to a product name,
           unless the line is a plain article number plus 5pcs */
        var last = parts[parts.length - 1];
        var n = parseInt(last.replace(/^x/i, ''), 10);
        if (/^x?\d+x?$/i.test(last) || (parts.length === 2 && /\d/.test(first) && n > 0)) {
          if (n > 0) qty = n;
          parts.pop();
        } else if (/^x$/i.test(last)) {
          parts.pop();
        }
      }
      lines.push({ code: first, text: parts.join(' '), qty: qty });
    });
    return lines;
  }

  function initQuickOrder() {
    var box = document.querySelector('[data-dtc-qo]');
    if (!box || box.hasAttribute('data-dtc-ready')) return;
    box.setAttribute('data-dtc-ready', '');
    var area = box.querySelector('textarea');
    var btn = box.querySelector('[data-dtc-qo-add]');
    var msg = box.querySelector('[data-dtc-qo-msg]');
    if (!area || !btn) return;

    function say(text, error) {
      if (!msg) return;
      msg.textContent = text;
      msg.classList.toggle('is-error', !!error);
      msg.hidden = !text;
    }

    /* sales reps (customer tag sales-rep) may also order single units, which
       only SparkLayer knows; dealers only get trade packs */
    var rep = box.hasAttribute('data-dtc-rep');

    /* the RRP of the articles just added, under the message */
    var rrpList = document.createElement('ul');
    rrpList.className = 'dtc-qo-rrp';
    rrpList.hidden = true;
    if (msg) msg.parentNode.insertBefore(rrpList, msg.nextSibling);

    function showRrp(lines) {
      loadArticleIndex().then(function (idx) {
        return Promise.all(lines.map(function (l) {
          var e = idx.sku[norm(l.sku)];
          if (!e || !(e.rrp > 0)) return null;
          return getPrice(e.pid, e.sku).then(function (p) {
            return rrpShown(e.rrp, p.currency) ? e : null;
          }, function () { return null; });
        }));
      }).then(function (found) {
        var seen = {};
        found.forEach(function (e) {
          if (!e || seen[e.sku]) return;
          seen[e.sku] = true;
          var li = document.createElement('li');
          li.textContent = e.sku + ' ' + e.title + (e.variant ? ', ' + e.variant : '') + ': RRP ' + rrpAmount(e.rrp);
          rrpList.appendChild(li);
        });
        rrpList.hidden = !rrpList.children.length;
      }).catch(function () {});
    }

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var parsed = parseQuickOrder(area.value);
      if (!parsed.length) { say('Enter at least one article number.', true); return; }
      btn.disabled = true;
      say('Adding ' + lineWord(parsed.length) + '...');
      rrpList.textContent = '';
      rrpList.hidden = true;

      var sent = [];
      var leftover = [];
      var notPack = [];
      var noEan = [];
      var unchecked = [];
      var asPack = [];
      var several = [];
      var noName = [];
      var manyNames = [];

      /* storefront lookup for codes the article index does not know, for
         example a product added since the index was cached */
      function viaSearch(l, isEan) {
        return findTradePack(l.code, isEan).then(function (sku) {
          if (sku) return { sku: sku, qty: l.qty };
          (isEan ? noEan : notPack).push(l.code);
          leftover.push(l.code + ' ' + l.qty);
          return null;
        }, function () {
          unchecked.push(l.code);
          leftover.push(l.code + ' ' + l.qty);
          return null;
        });
      }

      /* a line with words instead of an article number: one product name match
         is ordered, more than one has to be narrowed down */
      function viaName(l, idx, group) {
        var terms = splitTerms(l.text);
        var found = idx.all.filter(function (e) { return matchTerms(e.name, terms) && visibleToGroup(e, group); });
        leftover.push(l.text + ' ' + l.qty);
        if (!found.length) { noName.push(l.text); return null; }
        if (found.length > 1) {
          var examples = found.slice(0, 3).map(function (e) { return (e.base.split(/\s+/)[0] || e.sku) + ' ' + e.title + (e.variant ? ', ' + e.variant : ''); });
          manyNames.push(l.text + ' (' + found.length + ' articles, for example ' + examples.join('; ') + ')');
          return null;
        }
        leftover.pop();
        asPack.push(l.text + ' as ' + found[0].sku + ' (' + packWord(found[0]) + ')');
        return { sku: found[0].sku, qty: l.qty };
      }

      mapLimit(parsed, 4, function (l) {
        var isEan = /^\d{12,14}$/.test(l.code);
        var isCode = /\d/.test(l.code);
        if (rep && isCode && !isEan) return { sku: l.code, qty: l.qty };
        return Promise.all([loadArticleIndex(), customerGroup()]).then(function (res) {
          var idx = res[0];
          var group = res[1];
          var hit = lookupArticle(idx, l.code);
          if (hit) {
            hit.entries = hit.entries.filter(function (e) { return visibleToGroup(e, group); });
            if (!hit.entries.length) {
              notPack.push(l.code);
              leftover.push(l.code + ' ' + l.qty);
              return null;
            }
          }
          if (!hit && !isCode) return viaName(l, idx, group);
          if (!hit) return viaSearch(l, isEan);
          if (hit.entries.length > 1) {
            several.push(l.code + ' (' + hit.entries.map(function (e) { return e.sku + ', ' + packWord(e); }).join('; ') + ')');
            leftover.push(l.code + ' ' + l.qty);
            return null;
          }
          var e = hit.entries[0];
          if (hit.how === 'base') asPack.push(l.code + ' as ' + e.sku + ' (' + packWord(e) + ')');
          return { sku: e.sku, qty: l.qty };
        }, function () { return viaSearch(l, isEan); });
      }).then(function (lines) {
        sent = lines.filter(Boolean);
        return addToOrder(sent);
      }).then(function (count) {
        btn.disabled = false;
        var notes = [];
        if (notPack.length) {
          /* also what a mistyped article number gets: from the storefront a
             typo and a single unit look the same */
          notes.push(notPack.join(', ') + ': ' + (notPack.length === 1
            ? 'Not available as a trade pack. Check the article number.'
            : 'Not available as trade packs. Check the article numbers.'));
        }
        if (noEan.length) notes.push('EAN not found: ' + noEan.join(', ') + '.');
        if (several.length) notes.push('Sold in more than one trade pack, enter the number of the trade pack you want: ' + several.join(', ') + '.');
        if (noName.length) notes.push('No article found for: ' + noName.join(', ') + '.');
        if (manyNames.length) notes.push('More than one article matches, enter the article number or more of the name: ' + manyNames.join(', ') + '.');
        if (unchecked.length) notes.push('Could not check ' + unchecked.join(', ') + ', please try again.');
        if (count && asPack.length) notes.push('Matched: ' + asPack.join(', ') + '.');
        if (!count) {
          say(notes.length ? notes.join(' ') : 'None of these articles could be added to your order.', true);
          return;
        }
        area.value = leftover.join('\n');
        notes.unshift('Added ' + lineWord(count) + ' to your order. Check your order for any article that could not be added.');
        say(notes.join(' '), leftover.length > 0);
        showRrp(sent);
      }).catch(function () {
        btn.disabled = false;
        say('The order could not be updated. Please try again.', true);
      });
    });
  }

  function initReorder() {
    var btns = document.querySelectorAll('[data-dtc-reorder]:not([data-dtc-ready])');
    for (var i = 0; i < btns.length; i++) {
      (function (btn) {
        btn.setAttribute('data-dtc-ready', '');
        var busy = false;
        btn.addEventListener('click', function (e) {
          /* adjustQuantity adds on top of the cart, so a second click while
             busy (or while the confirmation shows) would double the order */
          if (busy) { e.preventDefault(); return; }
          var lines;
          try { lines = JSON.parse(btn.getAttribute('data-dtc-reorder')); } catch (err) { return; }
          if (!lines || !lines.length) return;
          e.preventDefault();
          busy = true;
          btn.setAttribute('aria-disabled', 'true');
          var label = btn.textContent;
          btn.textContent = 'Adding...';
          addToOrder(lines).then(function (count) {
            if (!count) throw new Error('Nothing added');
            var total = lines.map(function (l) { return l.sku; }).filter(function (s, i, a) { return s && a.indexOf(s) === i; }).length;
            btn.textContent = count < total
              ? 'Added ' + count + ' of ' + total + ' lines, view the order for the rest'
              : 'Added ' + lineWord(count) + ' to your order';
            setTimeout(function () {
              btn.textContent = label;
              btn.removeAttribute('aria-disabled');
              busy = false;
            }, 4000);
          }).catch(function () {
            btn.textContent = 'Could not add, view the order instead';
            btn.removeAttribute('data-dtc-reorder');
            btn.removeAttribute('aria-disabled');
            busy = false;
          });
        });
      })(btns[i]);
    }
  }

  /* ---------- 6. collection page: table view, grid view sort, storefront filters ---------- */

  var SORTS = ['name', 'article', 'price-asc', 'price-desc'];

  function compareText(a, b) {
    return String(a || '').localeCompare(String(b || ''), 'en', { numeric: true, sensitivity: 'base' });
  }

  /* missing values (no article number, no trade price) always go last */
  function compareMissingLast(a, b, fn) {
    var am = a == null || a === '';
    var bm = b == null || b === '';
    if (am || bm) return am === bm ? 0 : (am ? 1 : -1);
    return fn(a, b);
  }

  function byKey(key) {
    switch (key) {
      case 'name': return function (a, b) { return compareText(a.name, b.name); };
      case 'article': return function (a, b) { return compareMissingLast(a.art, b.art, compareText); };
      case 'price-asc': return function (a, b) { return compareMissingLast(a.price, b.price, function (x, y) { return x - y; }); };
      case 'price-desc': return function (a, b) { return compareMissingLast(a.price, b.price, function (x, y) { return y - x; }); };
      default: return function () { return 0; };
    }
  }

  /* items: [{ el, name, sku, art, price, pos }], sorted in place; ties keep
     the collection order. art is the original article number dealers know
     (b2b.base_sku), sku the trade pack SKU that prices and orders go by. */
  function sortItems(items, key) {
    var cmp = byKey(key);
    items.sort(function (a, b) {
      if (!key) return a.pos - b.pos;
      return cmp(a, b) || (key === 'name' ? 0 : compareText(a.name, b.name)) || a.pos - b.pos;
    });
    return items;
  }

  /* re-append els in this order without losing the focused field */
  function reorder(parent, els) {
    var active = document.activeElement;
    var keep = active && parent.contains(active) ? active : null;
    var frag = document.createDocumentFragment();
    els.forEach(function (el) { frag.appendChild(el); });
    parent.appendChild(frag);
    if (keep && document.activeElement !== keep) {
      try { keep.focus({ preventScroll: true }); } catch (e) { keep.focus(); }
    }
  }

  function initOrderTable() {
    var root = document.querySelector('[data-dtc-ot]');
    if (!root || root.hasAttribute('data-dtc-ready')) return;
    root.setAttribute('data-dtc-ready', '');

    var html = document.documentElement;
    var tbody = root.querySelector('[data-dtc-rows]');
    var filter = root.querySelector('[data-dtc-filter]');
    var countEl = root.querySelector('[data-dtc-count]');
    var loadingEl = root.querySelector('[data-dtc-loading]');
    var emptyEl = root.querySelector('[data-dtc-empty]');
    var summaryEl = root.querySelector('[data-dtc-summary]');
    var msgEl = root.querySelector('[data-dtc-msg]');
    var addBtn = root.querySelector('[data-dtc-add]');
    var clearBtn = root.querySelector('[data-dtc-clear]');
    var viewBtns = root.querySelectorAll('[data-dtc-view-btn]');
    var facetForm = root.querySelector('[data-dtc-facets]');
    var sortSel = root.querySelector('[data-dtc-sort]');
    var sortMsg = root.querySelector('[data-dtc-sortmsg]');
    var started = false;
    var observer = null;
    var sortKey = '';
    var sortToken = 0;
    var outside = {};
    var outsideCount = 0;
    var searchIndex = null;
    var searchLoad = null;

    /* kept for this browser session only: a price sort on a large collection
       asks SparkLayer for every trade price, so it should not stick for good */
    try {
      var savedSort = window.sessionStorage.getItem('dtcSort');
      if (SORTS.indexOf(savedSort) !== -1) sortKey = savedSort;
    } catch (e) {}
    if (sortSel) sortSel.value = sortKey;

    function currentView() { return html.getAttribute('data-dtc-view') === 'grid' ? 'grid' : 'table'; }

    function setView(view, remember) {
      html.setAttribute('data-dtc-view', view);
      if (remember) { try { window.localStorage.setItem('dtcView', view); } catch (e) {} }
      for (var i = 0; i < viewBtns.length; i++) {
        viewBtns[i].setAttribute('aria-pressed', viewBtns[i].getAttribute('data-dtc-view-btn') === view ? 'true' : 'false');
      }
      if (view === 'table') start();
      else if (sortKey) sortGrid();
    }

    for (var i = 0; i < viewBtns.length; i++) {
      viewBtns[i].addEventListener('click', function () { setView(this.getAttribute('data-dtc-view-btn'), true); });
    }

    function rows() { return tbody ? tbody.querySelectorAll('tr') : []; }

    /* resolves once the row has its price, is blocked, or SparkLayer gave up */
    function fillRow(tr) {
      if (tr.__dtcFill) return tr.__dtcFill;
      var sku = tr.getAttribute('data-sku');
      if (!sku) return Promise.resolve();
      if (observer) observer.unobserve(tr);
      var pack = parseInt(tr.getAttribute('data-pack'), 10) || 1;
      var priceEl = tr.querySelector('[data-dtc-price]');
      var unitEl = tr.querySelector('[data-dtc-unit]');
      tr.__dtcFill = getPrice(tr.getAttribute('data-pid'), sku).then(function (p) {
        tr.__dtcPrice = p;
        if (priceEl) {
          priceEl.textContent = money(p.price, p.currency);
          if (rrpShown(tr.getAttribute('data-rrp'), p.currency)) {
            priceEl.appendChild(make('span', 'dtc-ot__rrp', 'RRP ' + rrpAmount(tr.getAttribute('data-rrp'))));
          }
        }
        if (unitEl) unitEl.textContent = money(p.price / pack, p.currency);
        if (tr.classList.contains('is-picked')) updateSummary();
      }, function (err) {
        if (err && err.noPrice) { blockRow(tr); return; }
        /* SparkLayer did not answer: try this row once more when it is in view
           again or picked */
        if ((tr.__dtcTries = (tr.__dtcTries || 0) + 1) < 2) {
          tr.__dtcFill = null;
          if (observer) observer.observe(tr);
        }
      });
      return tr.__dtcFill;
    }

    /* no trade price for this dealer: the row cannot be ordered */
    function blockRow(tr) {
      var cell = tr.querySelector('.col-qty');
      if (!cell || !cell.querySelector('[data-dtc-qty]')) return;
      var wasPicked = tr.classList.contains('is-picked');
      tr.classList.remove('is-picked');
      tr.classList.add('is-blocked');
      cell.textContent = '';
      var na = document.createElement('span');
      na.className = 'dtc-ot__na';
      na.textContent = 'Not available to order';
      cell.appendChild(na);
      if (wasPicked) {
        updateSummary();
        say('One of the articles you picked is not available to order and has been taken off.', true);
      }
    }

    function watch(list) {
      for (var i = 0; i < list.length; i++) {
        if (observer) observer.observe(list[i]); else fillRow(list[i]);
      }
    }

    /* The observer's first report can go missing: the page scrolls inside
       Horizon's .page-wrapper, and on live no row was priced until the page
       was scrolled. Rows already in view (or within 300px) are priced
       directly; the observer still handles everything scrolled to later. */
    function fillVisible() {
      var h = window.innerHeight || document.documentElement.clientHeight;
      var list = rows();
      for (var i = 0; i < list.length; i++) {
        var tr = list[i];
        if (tr.__dtcFill || tr.hidden) continue;
        var r = tr.getBoundingClientRect();
        if (r.height > 0 && r.bottom >= -300 && r.top <= h + 300) fillRow(tr);
      }
    }

    /* ----- filter: this collection, plus the rest of the catalogue ----- */

    function make(tag, className, text) {
      var el = document.createElement(tag);
      if (className) el.className = className;
      if (text != null) el.textContent = text;
      return el;
    }

    /* the same row as sections/dtc-order-table.liquid, from the article index */
    function buildOutsideRow(e) {
      var base = e.base || '';
      var art = base || e.sku;
      var withVariant = e.title + (e.variant ? ' ' + e.variant : '');
      var tr = document.createElement('tr');
      tr.setAttribute('data-dtc-outside', '');
      tr.setAttribute('data-pid', e.pid);
      tr.setAttribute('data-sku', e.sku);
      tr.setAttribute('data-art', art.split(/\s+/)[0]);
      tr.setAttribute('data-pack', e.pack);
      tr.setAttribute('data-pos', 1000000000 + (++outsideCount));
      tr.setAttribute('data-name', withVariant);
      tr.setAttribute('data-search', e.hay);
      if (e.rrp > 0) tr.setAttribute('data-rrp', e.rrp);

      var pic = make('td', 'dtc-ot__pic col-thumb');
      if (e.img) {
        var img = make('img', 'dtc-ot__img');
        img.src = e.img;
        img.alt = '';
        img.width = 40;
        img.height = 40;
        img.loading = 'lazy';
        pic.appendChild(img);
      } else {
        pic.appendChild(make('span', 'dtc-ot__img'));
      }
      tr.appendChild(pic);

      var name = make('td', 'dtc-ot__name');
      var link = make('a', '', e.title);
      link.href = '/products/' + e.handle + (e.vid ? '?variant=' + e.vid : '');
      name.appendChild(link);
      if (e.variant) name.appendChild(make('span', 'dtc-ot__variant', e.variant));
      if (e.brand) name.appendChild(make('span', 'dtc-ot__brand', e.brand));
      name.appendChild(make('span', 'dtc-ot__meta', 'Article ' + art + ', ' + packWord(e) + (base ? ' (' + e.sku + ')' : '')));
      tr.appendChild(name);

      var code = make('td', 'code col-sku', art);
      if (base) {
        var packLine = make('span', 'dtc-ot__base', 'Pack ' + e.sku);
        packLine.title = 'Article number of the trade pack';
        code.appendChild(packLine);
      }
      tr.appendChild(code);
      tr.appendChild(make('td', 'code col-ean', e.ean || '-'));
      tr.appendChild(make('td', 'col-pack', e.pack + (e.pack === 1 ? ' unit' : ' units')));

      var cost = make('td', 'num dtc-ot__packcost col-cost');
      cost.setAttribute('data-dtc-price', '');
      tr.appendChild(cost);
      var unit = make('td', 'num col-unit');
      unit.setAttribute('data-dtc-unit', '');
      tr.appendChild(unit);

      var qtyCell = make('td', 'num col-qty');
      if (!e.available) {
        qtyCell.appendChild(make('span', 'dtc-ot__na', 'Out of stock'));
      } else {
        var wrap = make('span', 'dtc-qty');
        var less = make('button', '', '\u2212');
        less.type = 'button';
        less.tabIndex = -1;
        less.setAttribute('data-dtc-step', '-1');
        less.setAttribute('aria-label', 'One trade pack less');
        var input = document.createElement('input');
        input.type = 'number';
        input.min = '0';
        input.step = '1';
        input.value = '0';
        input.inputMode = 'numeric';
        input.setAttribute('data-dtc-qty', '');
        input.setAttribute('aria-label', 'Number of trade packs, ' + withVariant);
        var more = make('button', '', '+');
        more.type = 'button';
        more.tabIndex = -1;
        more.setAttribute('data-dtc-step', '1');
        more.setAttribute('aria-label', 'One trade pack more');
        wrap.appendChild(less);
        wrap.appendChild(input);
        wrap.appendChild(more);
        qtyCell.appendChild(wrap);
      }
      tr.appendChild(qtyCell);
      return tr;
    }

    /* one index for the whole session (loadArticleIndex), fetched on the first
       search, so typing sends no request per keystroke */
    function loadSearchIndex() {
      if (searchLoad) return searchLoad;
      searchLoad = Promise.all([loadArticleIndex(), customerGroup()]).then(function (res) {
        searchIndex = res[0].all.filter(function (e) { return visibleToGroup(e, res[1]); });
        applyFilter();
      }).catch(function () {
        searchLoad = null;
        if (countEl) countEl.textContent = 'Could not search the other brands. Showing this collection only.';
      });
      return searchLoad;
    }

    /* Rows for matches outside this collection. A row stays once made, so its
       quantity and price survive a new search; with an empty filter only the
       rows with a quantity stay in view, because they count in the summary. */
    function syncOutsideRows(terms, own) {
      var added = [];
      if (terms.length && searchIndex) {
        searchIndex.forEach(function (e) {
          if (own[e.sku] || outside[e.sku] || !matchTerms(e.hay, terms)) return;
          var tr = buildOutsideRow(e);
          outside[e.sku] = tr;
          tbody.appendChild(tr);
          added.push(tr);
        });
      }
      var shown = 0;
      Object.keys(outside).forEach(function (sku) {
        var tr = outside[sku];
        /* the page with this article in it has loaded after all */
        if (own[sku]) {
          tr.parentNode.removeChild(tr);
          delete outside[sku];
          return;
        }
        var input = tr.querySelector('[data-dtc-qty]');
        var ok = terms.length ? matchTerms(tr.getAttribute('data-search'), terms) : !!(input && parseInt(input.value, 10) > 0);
        tr.hidden = !ok;
        if (ok) shown++;
      });
      if (added.length) {
        watch(added);
        setTimeout(fillVisible, 100);
        if (sortKey) applyTableSort();
      }
      return shown;
    }

    function applyFilter() {
      var terms = splitTerms(filter ? filter.value : '');
      var list = tbody ? tbody.querySelectorAll('tr:not([data-dtc-outside])') : [];
      var own = {};
      var shown = 0;
      for (var i = 0; i < list.length; i++) {
        var ok = matchTerms(list[i].getAttribute('data-search') || '', terms);
        var rowSku = list[i].getAttribute('data-sku');
        if (rowSku) own[rowSku] = true;
        list[i].hidden = !ok;
        if (ok) shown++;
      }
      if (terms.length && !searchIndex) loadSearchIndex();
      var searching = terms.length > 0 && !searchIndex;
      var shownOutside = syncOutsideRows(terms, own);
      if (countEl) {
        var text = terms.length ? shown + ' of ' + list.length + ' articles' : list.length + ' articles';
        if (shownOutside) text += ', plus ' + shownOutside + ' outside this collection';
        else if (searching) text += ', searching all brands...';
        countEl.textContent = text;
      }
      if (emptyEl) emptyEl.hidden = shown + shownOutside > 0 || !terms.length || searching;
    }

    /* Rows of variants hidden from the customer's group (FishEx for the
       ...-zonder-fishex groups); the Liquid only knows group base. */
    function dropHiddenRows() {
      if (!tbody || !tbody.querySelector('tr[data-hidden-from]')) return;
      customerGroup().then(function (group) {
        if (!group) return;
        var list = tbody.querySelectorAll('tr[data-hidden-from]');
        for (var i = 0; i < list.length; i++) {
          var hiddenFrom = list[i].getAttribute('data-hidden-from').split(' ');
          var brand = list[i].getAttribute('data-brand');
          if (visibleToGroup({ hiddenFrom: hiddenFrom, brand: brand }, group)) continue;
          if (observer) observer.unobserve(list[i]);
          list[i].parentNode.removeChild(list[i]);
        }
        applyFilter();
        updateSummary();
      });
    }

    function picked() {
      var out = [];
      var inputs = tbody ? tbody.querySelectorAll('[data-dtc-qty]') : [];
      for (var i = 0; i < inputs.length; i++) {
        var qty = parseInt(inputs[i].value, 10);
        var tr = inputs[i].closest('tr');
        var on = qty > 0;
        tr.classList.toggle('is-picked', on);
        if (on) out.push({ tr: tr, input: inputs[i], sku: tr.getAttribute('data-sku'), qty: qty });
      }
      return out;
    }

    function updateSummary() {
      var list = picked();
      var packs = 0;
      var total = 0;
      var complete = true;
      var currency = 'EUR';
      list.forEach(function (l) {
        packs += l.qty;
        if (l.tr.__dtcPrice) { total += l.tr.__dtcPrice.price * l.qty; currency = l.tr.__dtcPrice.currency; }
        else { complete = false; fillRow(l.tr); }
      });
      if (addBtn) addBtn.disabled = !list.length;
      if (clearBtn) clearBtn.hidden = !list.length;
      if (!summaryEl) return;
      if (!list.length) { summaryEl.textContent = 'No trade packs selected yet'; return; }
      var text = lineWord(list.length) + ', ' + packs + (packs === 1 ? ' trade pack' : ' trade packs');
      summaryEl.textContent = '';
      var b = document.createElement('b');
      b.textContent = text;
      summaryEl.appendChild(b);
      summaryEl.appendChild(document.createTextNode(complete
        ? ', ' + money(total, currency) + ' excl. VAT'
        : ', total follows once all trade pack prices have loaded'));
    }

    function say(text, error) {
      if (!msgEl) return;
      msgEl.textContent = text;
      msgEl.classList.toggle('is-error', !!error);
      msgEl.hidden = !text;
    }

    function sortSay(text) { if (sortMsg) sortMsg.textContent = text; }

    /* ----- sort: table rows ----- */

    function sortTable() {
      if (!tbody) return;
      var items = Array.prototype.map.call(rows(), function (tr) {
        return {
          el: tr,
          name: tr.getAttribute('data-name') || '',
          sku: tr.getAttribute('data-sku') || '',
          art: tr.getAttribute('data-art') || tr.getAttribute('data-sku') || '',
          price: tr.__dtcPrice ? tr.__dtcPrice.price : null,
          pos: parseInt(tr.getAttribute('data-pos'), 10) || 0
        };
      });
      reorder(tbody, sortItems(items, sortKey).map(function (it) { return it.el; }));
    }

    /* Price sorts need the price of every row, not only the rows in view.
       The rows are priced through the shared queue and sorted once all have
       answered; a newer sort choice cancels the wait. */
    function applyTableSort() {
      var token = ++sortToken;
      if (sortKey.indexOf('price') === 0) {
        var todo = Array.prototype.filter.call(rows(), function (tr) {
          return tr.getAttribute('data-sku') && !tr.__dtcPrice && !tr.classList.contains('is-blocked');
        });
        if (todo.length) {
          var done = 0;
          sortSay('Loading trade prices to sort, 0 of ' + todo.length + '...');
          Promise.all(todo.map(function (tr) {
            return fillRow(tr).then(function () {
              done++;
              if (token === sortToken && (done % 25 === 0)) sortSay('Loading trade prices to sort, ' + done + ' of ' + todo.length + '...');
            });
          })).then(function () {
            if (token !== sortToken) return;
            sortTable();
            sortSay('');
          });
          return;
        }
      }
      sortTable();
      sortSay('');
    }

    /* ----- sort: grid cards (Horizon's product grid) ----- */

    var gridLoad = null;
    var gridOrder = null;

    function gridParts() {
      var list = document.querySelector('results-list[section-id]');
      var grid = list ? list.querySelector('[ref="grid"]') : null;
      return grid ? { list: list, grid: grid, sectionId: list.getAttribute('section-id') } : null;
    }

    function cardItems(grid) {
      return Array.prototype.slice.call(grid.querySelectorAll(':scope > li[data-product-id]'));
    }

    /* Horizon shows 24 products and loads the next page when the end of the
       grid scrolls into view. A sort has to see the whole collection, so this
       fetches the missing pages the same way (section rendering), puts every
       card in collection order and switches the infinite scroll off. */
    function loadAllCards() {
      if (gridLoad) return gridLoad;
      var parts = gridParts();
      if (!parts) return Promise.reject(new Error('No grid'));
      var last = parseInt(parts.grid.getAttribute('data-last-page'), 10) || 1;
      var sentinels = parts.list.querySelectorAll('[ref="viewMoreNext"], [ref="viewMorePrevious"], .pagination');
      for (var s = 0; s < sentinels.length; s++) sentinels[s].hidden = true;
      var present = {};
      cardItems(parts.grid).forEach(function (li) { present[li.getAttribute('data-page') || '1'] = true; });
      var missing = [];
      for (var p = 1; p <= last; p++) if (!present[p]) missing.push(p);
      var fetched = {};
      if (missing.length) sortSay('Loading all products to sort...');

      gridLoad = mapLimit(missing, 3, function (page) {
        var url = new URL(window.location.href);
        url.searchParams.set('section_id', parts.sectionId);
        url.searchParams.set('page', page);
        url.searchParams.delete('view');
        url.hash = '';
        return fetch(url.toString(), { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error('Request failed'); return r.text(); })
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var src = doc.querySelector('[ref="grid"]');
            fetched[page] = src ? Array.prototype.slice.call(src.querySelectorAll(':scope > li[data-product-id]')) : [];
          });
      }).then(function () {
        /* cards already on the page win over fetched copies, also when
           Horizon appended a page while these were loading */
        var live = {};
        cardItems(parts.grid).forEach(function (li) {
          var p = li.getAttribute('data-page') || '1';
          (live[p] = live[p] || []).push(li);
        });
        var seen = {};
        gridOrder = [];
        for (var p = 1; p <= last; p++) {
          (live[p] || fetched[p] || []).forEach(function (li) {
            var id = li.getAttribute('data-product-id');
            if (seen[id]) { if (li.parentNode === parts.grid) parts.grid.removeChild(li); return; }
            seen[id] = true;
            gridOrder.push(li.ownerDocument === document ? li : document.importNode(li, true));
          });
        }
        reorder(parts.grid, gridOrder);
        return gridOrder;
      });
      gridLoad.catch(function () { gridLoad = null; sortSay('Not all products could be loaded to sort. Reload the page to try again.'); });
      return gridLoad;
    }

    function cardKey(li, pos) {
      var pack = li.querySelector('[data-dtc-pack]');
      var title = li.querySelector('.product-card a[href*="/products/"]');
      return {
        el: li,
        pid: li.getAttribute('data-product-id'),
        name: pack ? pack.getAttribute('data-name') : (title ? title.textContent.trim() : ''),
        sku: pack ? pack.getAttribute('data-sort-sku') : '',
        art: pack ? (pack.getAttribute('data-sort-art') || pack.getAttribute('data-sort-sku')) : '',
        price: null,
        pos: pos
      };
    }

    function sortGrid() {
      var token = ++sortToken;
      var parts = gridParts();
      if (!parts) return;
      if (!sortKey && !gridOrder) return;
      loadAllCards().then(function (order) {
        if (token !== sortToken) return;
        var items = order.map(cardKey);
        if (sortKey.indexOf('price') !== 0) {
          reorder(parts.grid, sortItems(items, sortKey).map(function (it) { return it.el; }));
          sortSay('');
          return;
        }
        /* the price SparkLayer shows on the card: its first visible variant */
        var done = 0;
        sortSay('Loading trade prices to sort, 0 of ' + items.length + '...');
        mapLimit(items, 4, function (it) {
          if (!it.sku) return null;
          return getPrice(it.pid, it.sku).then(function (p) { it.price = p.price; }, function () {}).then(function () {
            done++;
            if (token === sortToken && done % 25 === 0) sortSay('Loading trade prices to sort, ' + done + ' of ' + items.length + '...');
          });
        }).then(function () {
          if (token !== sortToken) return;
          reorder(parts.grid, sortItems(items, sortKey).map(function (it) { return it.el; }));
          sortSay('');
        });
      }, function () {});
    }

    function applySort() {
      if (currentView() === 'table') { if (started) applyTableSort(); }
      else sortGrid();
    }

    if (sortSel) sortSel.addEventListener('change', function () {
      sortKey = SORTS.indexOf(sortSel.value) !== -1 ? sortSel.value : '';
      try { window.sessionStorage.setItem('dtcSort', sortKey); } catch (e) {}
      applySort();
    });

    /* ----- storefront filters: reload the collection with the chosen value ----- */

    function goFacets() {
      var url = new URL(facetForm.getAttribute('action') || window.location.pathname, window.location.href);
      new FormData(facetForm).forEach(function (value, name) {
        if (value !== '') url.searchParams.append(name, value);
      });
      window.location.href = url.toString();
    }

    if (facetForm) {
      facetForm.addEventListener('change', function (e) {
        if (e.target.matches && e.target.matches('[data-dtc-facet]')) goFacets();
      });
      facetForm.addEventListener('submit', function (e) {
        e.preventDefault();
        goFacets();
      });
    }

    function loadOtherPages() {
      var current = parseInt(tbody.getAttribute('data-page'), 10) || 1;
      var pages = parseInt(tbody.getAttribute('data-pages'), 10) || 1;
      var sectionId = root.getAttribute('data-section-id');
      if (pages < 2 || !sectionId) return Promise.resolve();
      var firstOwn = tbody.firstElementChild;
      var chain = Promise.resolve();
      var done = 1;

      function load(page) {
        var url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);
        url.searchParams.set('page', page);
        url.searchParams.delete('view');
        return fetch(url.toString(), { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error('Request failed'); return r.text(); })
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var src = doc.querySelector('[data-dtc-rows]');
            if (!src) return;
            var added = [];
            while (src.firstElementChild) {
              var tr = document.importNode(src.firstElementChild, true);
              src.removeChild(src.firstElementChild);
              if (page < current) tbody.insertBefore(tr, firstOwn); else tbody.appendChild(tr);
              added.push(tr);
            }
            watch(added);
            done++;
            if (loadingEl) loadingEl.textContent = 'Loading the rest of this collection, page ' + done + ' of ' + pages + '...';
            applyFilter();
          });
      }

      if (loadingEl) { loadingEl.hidden = false; loadingEl.textContent = 'Loading the rest of this collection...'; }
      for (var p = 1; p <= pages; p++) {
        if (p === current) continue;
        (function (page) { chain = chain.then(function () { return load(page); }); })(p);
      }
      return chain.then(function () {
        if (loadingEl) loadingEl.hidden = true;
      }).catch(function () {
        if (loadingEl) loadingEl.textContent = 'Not all products could be loaded. Reload the page to try again.';
      });
    }

    function start() {
      if (started || !tbody) return;
      started = true;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (!en.isIntersecting) return;
            observer.unobserve(en.target);
            fillRow(en.target);
          });
        }, { rootMargin: '300px 0px' });
      }
      watch(rows());
      applyFilter();
      setTimeout(fillVisible, 400);
      setTimeout(fillVisible, 2000);
      if (sortKey) applyTableSort();
      dropHiddenRows();
      /* rows of the other pages arrive in collection order; sort again once
         they are all in */
      loadOtherPages().then(function () {
        dropHiddenRows();
        if (sortKey) applyTableSort();
      });
    }

    if (filter) {
      filter.addEventListener('input', applyFilter);
      filter.addEventListener('focus', loadSearchIndex);
    }

    if (tbody) {
      tbody.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-dtc-step]') : null;
        if (!btn) return;
        var input = btn.parentNode.querySelector('[data-dtc-qty]');
        if (!input) return;
        var next = (parseInt(input.value, 10) || 0) + parseInt(btn.getAttribute('data-dtc-step'), 10);
        input.value = next > 0 ? next : 0;
        say('');
        updateSummary();
      });
      tbody.addEventListener('input', function (e) {
        if (!e.target.matches || !e.target.matches('[data-dtc-qty]')) return;
        say('');
        updateSummary();
      });
      tbody.addEventListener('change', function (e) {
        if (!e.target.matches || !e.target.matches('[data-dtc-qty]')) return;
        var n = parseInt(e.target.value, 10);
        e.target.value = n > 0 ? n : 0;
        updateSummary();
      });
      tbody.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' || !e.target.matches || !e.target.matches('[data-dtc-qty]')) return;
        e.preventDefault();
        var inputs = Array.prototype.filter.call(tbody.querySelectorAll('[data-dtc-qty]'), function (el) {
          return !el.closest('tr').hidden;
        });
        var idx = inputs.indexOf(e.target);
        var target = inputs[idx + (e.shiftKey ? -1 : 1)];
        if (target) { target.focus(); target.select(); }
      });
      tbody.addEventListener('focusin', function (e) {
        if (e.target.matches && e.target.matches('[data-dtc-qty]')) e.target.select();
      });
    }

    if (clearBtn) clearBtn.addEventListener('click', function () {
      picked().forEach(function (l) { l.input.value = 0; });
      say('');
      updateSummary();
      applyFilter();
    });

    if (addBtn) addBtn.addEventListener('click', function () {
      var list = picked();
      if (!list.length) return;
      addBtn.disabled = true;
      say('Adding ' + lineWord(list.length) + '...');
      addToOrder(list).then(function (count) {
        if (!count) throw new Error('Nothing added');
        list.forEach(function (l) { l.input.value = 0; });
        updateSummary();
        applyFilter();
        say(count < list.length
          ? 'Added ' + count + ' of ' + list.length + ' lines to your order. Check your order for the articles that could not be added.'
          : 'Added ' + lineWord(count) + ' to your order.', count < list.length);
      }).catch(function () {
        updateSummary();
        say('The order could not be updated. Your quantities are still here, please try again.', true);
      });
    });

    setView(currentView(), false);
  }

  /* ---------- 7. header: number of lines in the SparkLayer order ---------- */

  /* SparkLayer calls spark.options.onCartUpdate after every change to its
     cart (also from its own drawer) and onCartLoad when it fetches the cart.
     Both are wrapped here, keeping whatever hook was there. The badge shows
     the number of lines; the last count is kept for the next page so the
     badge does not wait for SparkLayer. */
  var CART_COUNT_KEY = 'dtcOrderLines';

  function cartLines(cart) { return cart && Array.isArray(cart.items) ? cart.items.length : null; }

  function showCartCount(n) {
    var known = typeof n === 'number' && n >= 0;
    var badges = document.querySelectorAll('[data-dtc-cart-count]');
    var texts = document.querySelectorAll('[data-dtc-cart-count-text]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = known ? (n > 99 ? '99+' : String(n)) : '';
      badges[i].hidden = !(known && n > 0);
    }
    for (var t = 0; t < texts.length; t++) {
      texts[t].textContent = known && n > 0 ? lineWord(n) + ' in your order' : '';
    }
  }

  function onSparkCart(cart) {
    var n = cartLines(cart);
    if (n == null) return;
    try { window.sessionStorage.setItem(CART_COUNT_KEY, String(n)); } catch (e) {}
    showCartCount(n);
  }

  function hookSparkCart(spark) {
    var o = spark && spark.options;
    if (!o || o.__dtcCartHook) return;
    o.__dtcCartHook = true;
    ['onCartUpdate', 'onCartLoad'].forEach(function (name) {
      var prev = o[name];
      o[name] = function (cart) {
        try { onSparkCart(cart); enableSparkSlots(spark); } catch (e) {}
        return typeof prev === 'function' ? prev.apply(this, arguments) : undefined;
      };
    });
  }

  /* SparkLayer's own cart cache can still hold an old cart right after a page
     load, so the count comes from a fresh getCart (which also runs the
     onCartLoad hook). The cache is only the fallback when that fails. */
  function readSparkCart(spark) {
    var fromCache = function () {
      try { onSparkCart(typeof spark.getCartCache === 'function' ? spark.getCartCache() : null); } catch (e) {}
    };
    if (typeof spark.getCart !== 'function') { fromCache(); return; }
    withTimeout(spark.getCart(), 8000).then(function (cart) {
      if (cartLines(cart) != null) onSparkCart(cart); else fromCache();
    }, fromCache);
  }

  function initCartCount() {
    if (!document.querySelector('[data-dtc-cart-count]')) return;
    var cached = null;
    try { cached = window.sessionStorage.getItem(CART_COUNT_KEY); } catch (e) {}
    if (cached !== null && /^\d+$/.test(cached)) showCartCount(parseInt(cached, 10));
    whenSpark().then(function (spark) {
      hookSparkCart(spark);
      readSparkCart(spark);
    }, function () { showCartCount(null); });
    /* back and forward cache: the order may have changed on another page */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted && sparkState === 'ready') readSparkCart(window.spark);
    });
  }

  /* ---------- 8. search page: original article number matches ---------- */

  /* Shopify search finds trade pack SKUs and EANs itself, but not the
     original article number (b2b.base_sku). When the search term is one, the
     trade packs it is sold in are listed above the results. */
  function initSearchMatch() {
    if (!/\/search\/?$/.test(window.location.pathname)) return;
    var q = (new URLSearchParams(window.location.search).get('q') || '').trim();
    if (!/^[A-Za-z0-9][A-Za-z0-9.\/-]{2,24}$/.test(q)) return;
    var anchor = document.querySelector('results-list') || document.querySelector('#MainContent .shopify-section:last-child');
    if (!anchor) return;
    loadArticleIndex().then(function (idx) {
      var hit = lookupArticle(idx, q);
      if (!hit || hit.how !== 'base') return;
      var wrap = document.createElement('div');
      wrap.className = 'section section--page-width';
      var box = document.createElement('section');
      box.className = 'dtc-match';
      box.setAttribute('aria-labelledby', 'dtc-match-title');
      wrap.appendChild(box);
      var h = document.createElement('h2');
      h.id = 'dtc-match-title';
      h.className = 'dtc-match__title';
      h.textContent = 'Article ' + q + ' is sold as';
      box.appendChild(h);
      var ul = document.createElement('ul');
      ul.className = 'dtc-match__list';
      hit.entries.forEach(function (e) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = '/products/' + e.handle + (e.vid ? '?variant=' + e.vid : '');
        a.textContent = e.title + (e.variant ? ', ' + e.variant : '');
        var meta = document.createElement('span');
        meta.className = 'dtc-match__meta';
        meta.textContent = packWord(e).charAt(0).toUpperCase() + packWord(e).slice(1) + ' (' + e.sku + ')';
        li.appendChild(a);
        li.appendChild(meta);
        ul.appendChild(li);
      });
      box.appendChild(ul);
      anchor.parentNode.insertBefore(wrap, anchor);
    }, function () {});
  }

  /* ---------- 9. order page in SparkLayer's drawer: document buttons ---------- */

  /* Sits in SparkLayer's purchase summary slot (snippets/dtc-spark-slots.liquid),
     which carries the purchase ids on its wrapper. Drafts awaiting the merchant
     and quotes have no order behind them yet, so they get no buttons. */
  if (window.customElements && !customElements.get('dtc-order-docs')) {
    customElements.define('dtc-order-docs', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        var data = (this.parentNode && this.parentNode.dataset) || {};
        if (!data.sparkId) return;
        whenSpark().then(function (spark) {
          return withTimeout(spark.fetch(
            'query dtcPurchaseType($impersonatingCustomerId: String, $sparklayerId: String) { purchase(impersonatingCustomerId: $impersonatingCustomerId, sparklayerId: $sparklayerId) { type } }',
            { sparklayerId: data.sparkId }
          ), 8000);
        }).then(function (r) { return r.json(); }).then(function (d) {
          if (d.data.purchase.type !== 'ORDER') return;
          var links = self.querySelectorAll('a[data-doc]');
          for (var i = 0; i < links.length; i++) {
            var url = new URL('/collections/all', window.location.origin);
            url.searchParams.set('view', 'dtc-order-document');
            url.searchParams.set('doc', links[i].getAttribute('data-doc'));
            url.searchParams.set('order', data.visibleId || '');
            url.searchParams.set('id', data.sparkId);
            links[i].href = url.pathname + url.search;
          }
          self.hidden = false;
        }).catch(function () {});
      }
    });
  }

  /* ---------- 10. returns page ---------- */

  function typeInto(input, value) {
    if (!input || input.value || !value) return;
    Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  /* A SparkLayer form field cannot default to the signed-in customer, so the
     fields company and email are filled in here, with the dealer being served
     when a sales rep is signed in. Waits until the form's fields are in the DOM,
     then keeps trying for PREFILL_RETRY, because the form can still redraw right
     after it appears. Only fills empty fields and stops as soon as the customer
     types. If it fails the fields stay empty and required. */
  var PREFILL_RETRY = 5000;
  var PREFILL_WAIT = 30000;

  function prefillForm(form) {
    var customer = null;
    var started = Date.now();
    var until = 0;
    var typed = false;

    whenSpark().then(function (spark) {
      return withTimeout(spark.fetch(
        'query dtcFormCustomer($impersonatingCustomerId: String) { loggedInCustomer(impersonatingCustomerId: $impersonatingCustomerId) { companyName email } }',
        {}
      ), 8000);
    }).then(function (r) { return r.json(); }).then(function (d) {
      customer = d.data.loggedInCustomer;
    }).catch(function () {});

    (function fill() {
      if (typed) return;
      var root = form.shadowRoot;
      var company = root && root.querySelector('input[name="company"]');
      var email = root && root.querySelector('input[name="email"]');
      if (company && email && customer) {
        if (!until) {
          until = Date.now() + PREFILL_RETRY;
          root.addEventListener('input', function (e) { if (e.isTrusted) typed = true; });
        }
        typeInto(company, customer.companyName);
        typeInto(email, customer.email);
      }
      if (until ? Date.now() < until : Date.now() - started < PREFILL_WAIT) setTimeout(fill, 250);
    })();
  }

  /* sections/dtc-returns.liquid: a button opens its panel without a reload.
     The SparkLayer form and its script are only loaded when first needed. */
  function initReturns() {
    var root = document.querySelector('[data-dtc-returns]');
    if (!root || root.hasAttribute('data-dtc-ready')) return;
    root.setAttribute('data-dtc-ready', '');
    var choices = root.querySelectorAll('[data-dtc-returns-choice]');
    var panels = root.querySelectorAll('[data-dtc-returns-panel]');

    function loadForm(panel) {
      var holder = panel.querySelector('[data-dtc-returns-form]');
      if (!holder || holder.firstElementChild) return;
      if (!document.querySelector('script[src*="spark-form.v1.js"]')) {
        var script = document.createElement('script');
        script.src = 'https://cdn.sparklayer.io/spark-form.v1.js';
        document.head.appendChild(script);
      }
      var form = document.createElement('spark-form');
      form.setAttribute('form-id', holder.getAttribute('data-dtc-returns-form'));
      form.setAttribute('site-id', root.getAttribute('data-site-id'));
      form.setAttribute('hide-title', 'true');
      holder.appendChild(form);
      prefillForm(form);
    }

    function show(target) {
      Array.prototype.forEach.call(choices, function (btn) {
        btn.setAttribute('aria-expanded', btn.getAttribute('aria-controls') === target ? 'true' : 'false');
      });
      Array.prototype.forEach.call(panels, function (panel) {
        panel.hidden = panel.id !== target;
        if (!panel.hidden) loadForm(panel);
      });
    }

    Array.prototype.forEach.call(choices, function (btn) {
      btn.addEventListener('click', function () { show(btn.getAttribute('aria-controls')); });
    });

    /* The main menu links to /pages/returns#delivery and #return, also while this
       page is already open: there the link only changes the hash, or nothing when
       the hash is the same, and the mobile menu has to be closed by hand. */
    function showFromHash() {
      var hash = window.location.hash;
      if (hash === '#return') {
        show(null);
      } else if (hash === '#delivery') {
        var delivery = root.querySelector('[data-dtc-returns-choice][aria-controls$="delivery_problem"]');
        if (delivery) show(delivery.getAttribute('aria-controls'));
      } else {
        return;
      }
      var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var choiceRow = root.querySelector('.dtc-returns__choices');
      if (choiceRow) choiceRow.scrollIntoView({ block: 'start', behavior: calm ? 'auto' : 'smooth' });
    }

    showFromHash();
    window.addEventListener('hashchange', showFromHash);
    document.addEventListener('click', function (e) {
      if (!e.target.closest || !e.target.closest('a[href*="/pages/returns#"]')) return;
      Array.prototype.forEach.call(document.querySelectorAll('header-drawer'), function (drawer) {
        if (drawer.isOpen) drawer.close();
      });
      setTimeout(showFromHash, 0);
    });
  }

  /* ---------- start ---------- */

  function init(scope) {
    markSpark(scope);
    initPacks(scope);
    initRrp(scope);
    initPlaceholders(scope);
    initQuickOrder();
    initReorder();
    initOrderTable();
    initReturns();
  }

  /* Only look at elements that were added since the last run. Text changes
     (prices filling in, counters) add text nodes only and are skipped, so
     loading the order table does not rescan the whole page. */
  var pending = [];
  var scheduled = false;
  function collect(records) {
    for (var r = 0; r < records.length; r++) {
      var nodes = records[r].addedNodes;
      for (var n = 0; n < nodes.length; n++) {
        if (nodes[n].nodeType === 1) pending.push(nodes[n]);
      }
    }
    if (!pending.length || scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      var batch = pending.splice(0);
      for (var i = 0; i < batch.length; i++) {
        if (batch[i].isConnected) init(batch[i]);
      }
    }, 150);
  }

  function boot() {
    init(document);
    initCartCount();
    initSearchMatch();
    try {
      new MutationObserver(collect).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    [1000, 3000, 6000].forEach(function (ms) {
      setTimeout(function () {
        markSpark(document);
        enableSparkSlots(window.spark);
      }, ms);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
